import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

import { visuallyHidden } from "@mui/utils";
import ScrollEffect from "../../effects/ScrollEffect";
import {
  useModalHistory,
  ACTIONS as MODAL_ACTIONS,
} from "../../context/ModalHistoryProvider";
import LoadEffect from "../../effects/LoadEffect";
import { Radio, Skeleton } from "@mui/material";
import StackStyle from "../Styled/Stack.style";

import styled from "styled-components";
import {
  useDealsContext,
  ACTIONS as DEAL_ACTIONS,
} from "../../context/DealsProvider";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const StyledTableCell = styled(TableCell)`
  span {
    width: max-content;
  }
`;

function EnhancedTableHead(props) {
  const {
    headers,
    order,
    orderBy,
    onRequestSort,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell padding={"checkbox"}>
          Select
        </StyledTableCell>
        {headers?.map((header) => (
          <StyledTableCell
            key={header.id}
            padding={"checkbox"}
            sortDirection={orderBy === header.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === header.id}
              direction={orderBy === header.id ? order : "asc"}
              onClick={createSortHandler(header.id)}
              sx={{ textTransform: "capitalize" }}
            >
              {orderBy === header.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
              {header.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  headers: PropTypes.array.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const EnhancedTableToolbar = ({selected}) => {
  const [, dispatchDeals] = useDealsContext();

  return (
    <Toolbar
      sx={{
        minHeight: "unset !important",
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(selected && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Search result
      </Typography>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  selected: PropTypes.string.isRequired,
};

export default function TableRadioSelect({ headers, rows, id, resultItemID, setResultItemID }) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [, dispatchDeals] = useDealsContext();
  const [, dispatchModalHistory] = useModalHistory();
  const pushEnd = (modal) =>
    dispatchModalHistory({
      type: MODAL_ACTIONS.PUSH_END,
      payload: { value: modal },
    });
  const popEnd = () =>
    dispatchModalHistory({
      type: MODAL_ACTIONS.POP_END,
    });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (event, id) => {
    setResultItemID(id);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => resultItemID === id;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%" }}>
        <EnhancedTableToolbar selected={resultItemID} />
        <TableContainer>
          <ScrollEffect width="100%" height="390px">
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="Deals"
              size="small"
              padding="checkbox"
              stickyHeader
            >
              <EnhancedTableHead
                headers={headers}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <LoadEffect
                condition={rows.length !== 0}
                fallback={
                  <StackStyle gap="5px">
                    <Skeleton variant="text" width="60vw" height="25px" />
                    <Skeleton variant="text" width="50vw" height="15px" />
                    <Skeleton variant="text" width="60vw" height="25px" />
                    <Skeleton variant="text" width="50vw" height="15px" />
                    <Skeleton variant="text" width="60vw" height="25px" />
                    <Skeleton variant="text" width="50vw" height="15px" />
                    <Skeleton variant="text" width="60vw" height="25px" />
                    <Skeleton variant="text" width="50vw" height="15px" />
                    <Skeleton variant="text" width="60vw" height="25px" />
                    <Skeleton variant="text" width="50vw" height="15px" />
                  </StackStyle>
                }
              >
                <TableBody>
                  {stableSort(rows, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const isItemSelected = isSelected(row[id]);
                      const labelId = `enhanced-table-radio-${index}`;

                      return (
                        <TableRow
                          hover
                          role="radio"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row[id]}
                          selected={isItemSelected}
                        >
                          <TableCell>
                            <Radio
                              checked={isItemSelected}
                              onChange={() => setResultItemID(row[id])}
                              inputProps={{ "aria-labelledby": labelId }}
                            />
                          </TableCell>
                          {Object.keys(row).map((key) => (
                            <StyledTableCell
                              onClick={(event) => handleClick(event, row[id])}
                            >
                              {row[key]}
                            </StyledTableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: 33 * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </LoadEffect>
            </Table>
          </ScrollEffect>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, rows?.length]}
          component="div"
          count={rows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
